import * as React from 'react';
import { useEffect } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import { getNextPageParam } from '@/lib/constants/pagination';
import { cn } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

const multiSelectVariants = cva(
  'm-1 px-1.5 py-1 text-xs font-normal transition delay-150 duration-300 ease-in-out',
  {
    variants: {
      variant: {
        default: 'border bg-input text-foreground hover:bg-primary/20',
        secondary:
          'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        inverted: 'inverted',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface MultiSelectOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional avatar image URL. Pass null to show initials only. */
  avatarLink?: string | null;
  /** Whether this entity has been deleted (e.g. a deleted team member). */
  isDeleted?: boolean;
}

interface MultiSelectWithPaginationProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * Query key for the infinite query. Must be stable (defined as a constant).
   */
  queryKey: unknown[];
  /**
   * Function to load items with cursor-based pagination and optional search.
   */
  loadItems: (params: {
    searchString?: string;
    cursor: number | null;
  }) => Promise<PaginatedResponse<DropdownSearchItem>>;
  /**
   * Pre-populated options merged with API results.
   * Use to ensure selected items (including deleted ones) always render correctly.
   */
  initialOptions?: MultiSelectOption[];
  /**
   * Currently selected values (controlled).
   */
  value: string[];
  /**
   * Callback fired when the selection changes.
   */
  onValueChange: (value: string[], selectedItems: MultiSelectOption[]) => void;
  /** Placeholder text shown when nothing is selected. */
  placeholder?: string;
  /** Maximum number of badges to display in the trigger before summarizing. */
  maxCount?: number;
  /** Additional class names. */
  className?: string;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** Debounce delay in ms for the search input. Defaults to 300. */
  debounceMs?: number;
}

const DEBOUNCE_DEFAULT_MS = 300;

/**
 * Multi-select with cursor-based infinite scroll pagination.
 * Lazily loads options from the API on first open, with debounced search.
 * Reuses the `useInfiniteScroll` hook (same pattern as FilterDynamicAutocompletePaginated).
 */
export const MultiSelectWithPagination = React.forwardRef<
  HTMLButtonElement,
  MultiSelectWithPaginationProps
>(
  (
    {
      queryKey,
      loadItems,
      initialOptions = [],
      value,
      onValueChange,
      variant,
      placeholder,
      maxCount = 3,
      className,
      disabled = false,
      debounceMs = DEBOUNCE_DEFAULT_MS,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common');

    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [hasOpened, setHasOpened] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    const [debouncedSearch, setDebouncedSearch] = React.useState('');

    const debouncedSetSearch = useDebouncedCallback((val: string) => {
      setDebouncedSearch(val);
    }, debounceMs);

    const handleSearchChange = (val: string) => {
      setSearchValue(val);
      debouncedSetSearch(val);
    };

    const handleOpenChange = (open: boolean) => {
      setIsPopoverOpen(open);
      if (open && !hasOpened) {
        setHasOpened(true);
      }
    };

    const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
      queryKey: [...queryKey, debouncedSearch],
      enabled: hasOpened,
      queryFn: ({ pageParam }) =>
        loadItems({
          searchString: debouncedSearch || undefined,
          cursor: (pageParam as number | null) ?? null,
        }),
      getNextPageParam,
      initialPageParam: null,
      staleTime: 5 * 60 * 1000,
    });

    const { setContainerElement, setSentinelElement } = useInfiniteScroll({
      hasNextPage: hasNextPage ?? false,
      isFetching,
      onLoadMore: fetchNextPage,
    });

    // Build API options from all loaded pages
    const apiOptions: MultiSelectOption[] = React.useMemo(() => {
      if (!data) return [];
      return data.pages.flatMap(page =>
        page.items.map((item: DropdownSearchItem) => ({
          value: item.id,
          label: item.name,
          avatarLink: item.avatarLink ?? null,
        }))
      );
    }, [data]);

    // Merge API options with initialOptions so pre-selected items always display
    const apiValueSet = React.useMemo(() => new Set(apiOptions.map(o => o.value)), [apiOptions]);
    const mergedOptions: MultiSelectOption[] = React.useMemo(
      () => [...apiOptions, ...initialOptions.filter(o => !apiValueSet.has(o.value))],
      [apiOptions, initialOptions, apiValueSet]
    );

    // Keep a stable reference of all known options for resolving selected items
    const optionsMapRef = React.useRef<Map<string, MultiSelectOption>>(new Map());
    useEffect(() => {
      mergedOptions.forEach(o => optionsMapRef.current.set(o.value, o));
    }, [mergedOptions]);

    const selectedSet = React.useMemo(() => new Set(value), [value]);

    const toggleOption = (optionValue: string) => {
      const newValues = selectedSet.has(optionValue)
        ? value.filter(v => v !== optionValue)
        : [...value, optionValue];
      const selectedItems = newValues
        .map(v => optionsMapRef.current.get(v))
        .filter((o): o is MultiSelectOption => o !== undefined);
      onValueChange(newValues, selectedItems);
    };

    const handleClear = () => {
      onValueChange([], []);
    };

    const handleTogglePopover = () => {
      if (!disabled) {
        handleOpenChange(!isPopoverOpen);
      }
    };

    // Filter displayed options in the dropdown based on search
    const filteredOptions = React.useMemo(() => {
      if (!debouncedSearch) return mergedOptions;
      const lower = debouncedSearch.toLowerCase();
      return mergedOptions.filter(o => o.label.toLowerCase().includes(lower));
    }, [mergedOptions, debouncedSearch]);

    return (
      <Popover
        open={disabled ? false : isPopoverOpen}
        onOpenChange={disabled ? undefined : handleOpenChange}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            {...props}
            disabled={disabled}
            onClick={handleTogglePopover}
            className={cn(
              'flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto',
              'disabled:border-input disabled:opacity-100 disabled:bg-muted disabled:pointer-events-none',
              className
            )}
          >
            {value.length > 0 ? (
              <div className="flex w-full min-w-0 items-center justify-between">
                <div className="flex min-w-0 flex-wrap items-center">
                  {value.slice(0, maxCount).map(val => {
                    const option =
                      optionsMapRef.current.get(val) ?? mergedOptions.find(o => o.value === val);
                    const displayLabel = option?.label ?? val;
                    const hasAvatar = option && 'avatarLink' in option;

                    return (
                      <Badge
                        key={val}
                        className={cn('max-w-full', multiSelectVariants({ variant }))}
                      >
                        {hasAvatar && (
                          <div className="mr-1.5">
                            <EntityAvatar
                              image={option?.avatarLink}
                              name={displayLabel}
                              size="xs"
                              shape="circle"
                              shouldDownscale={false}
                            />
                          </div>
                        )}
                        <span className="truncate">{displayLabel}</span>
                        {!disabled && (
                          <XIcon
                            className="ml-0.5 h-4 w-4 cursor-pointer"
                            onClick={event => {
                              event.stopPropagation();
                              toggleOption(val);
                            }}
                          />
                        )}
                      </Badge>
                    );
                  })}
                  {value.length > maxCount && (
                    <Badge
                      className={cn(
                        'bg-transparent text-foreground border-foreground/1',
                        multiSelectVariants({ variant })
                      )}
                    >
                      {t('moreSelected', { count: value.length - maxCount })}
                    </Badge>
                  )}
                </div>
                <ChevronDown
                  className={cn('h-4 mx-2 text-muted-foreground', disabled && 'cursor-default')}
                />
              </div>
            ) : (
              <div className="mx-auto flex w-full items-center justify-between">
                <span className="mx-3 text-sm font-normal text-muted-foreground">
                  {isLoading && hasOpened ? t('loading') : (placeholder ?? t('selectOptions'))}
                </span>
                <ChevronDown
                  className={cn('h-4 text-muted-foreground mx-2', disabled && 'cursor-default')}
                />
              </div>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex max-h-80 w-auto min-w-80 flex-col p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command className="flex flex-1 flex-col" shouldFilter={false}>
            <CommandInput
              placeholder={t('search')}
              value={searchValue}
              onValueChange={handleSearchChange}
            />
            <CommandList className="flex-1 overflow-y-auto" ref={setContainerElement}>
              {isLoading || isFetching ? (
                <p className="p-2 text-sm text-muted-foreground">{t('loading')}</p>
              ) : filteredOptions.length === 0 ? (
                <CommandEmpty>{t('noResultsFound')}</CommandEmpty>
              ) : null}

              <CommandGroup>
                {filteredOptions.map(option => {
                  const isSelected = selectedSet.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <CheckIcon className="h-4 w-4" />
                      </div>
                      {'avatarLink' in option && (
                        <EntityAvatar
                          image={option.avatarLink}
                          name={option.label}
                          size="xs"
                          shape="circle"
                          shouldDownscale={false}
                        />
                      )}
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>

              {/* Sentinel for infinite scroll */}
              {hasNextPage && <div ref={setSentinelElement} className="h-6" />}
            </CommandList>
          </Command>

          <div className="border-t bg-background p-3">
            <div className="flex items-center justify-between gap-4">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 flex-shrink-0"
                onClick={handleClear}
                disabled={value.length === 0}
              >
                {t('filters.clearAll')}
              </Button>
              <p className="flex-shrink-0 text-sm text-muted-foreground">
                {t('filters.selected', { count: value.length })}
              </p>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelectWithPagination.displayName = 'MultiSelectWithPagination';
